// The items-list inputs of a choice question -- radio, checkbox and the families drawn as answer
// variants -- ported from the shared coffee list view (ItemsListView#calcItemsListProps).
//
// It was reached through the extended open-end's calculator for a while, which is how that
// question's own wording ended up over these columns. Each family states its own rule here.

import { encodeLabel, formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { getFeature, getModelValue, resolveTrait } from '@aytm/app/stats/lib/stat-page'
import {
  isClickable,
  isRType as calcIsRType,
  isTraitType as calcIsTraitType,
  processText
} from '@aytm/app/stats/page/components/lib/utils'
import { hasBase as answerHasBase } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/chartModes'
import {
  answerSettled,
  buildOnCommentsClick,
  calcCount,
  screenerState,
  screenerKind
} from '@aytm/app/stats/page/question-views/oe/lib/table-utils'
import { colorIndexOf } from '@aytm/app/stats/page/question-views/components/lib/color-index'
import { calcTraitTable } from '@aytm/app/stats/page/question-views/components/lib/trait-table'
import { calcIgnoreAmount, calcIgnorePercents } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const traitValuesOf = trait => {
  if (null == trait) return []

  return (trait.values || trait.answerVariants || []).map(value => ({
    id: (Object.hasOwn(value, 'id') && value.id) || (Object.hasOwn(value, 'position') && `a${value.position}`) || value,
    name:
      (Object.hasOwn(value, 'name') && encodeLabel(value.name.en || value.name)) ||
      (Object.hasOwn(value, 'position') && `A${value.position}`) ||
      value
  }))
}

// What the progress column says it is measuring, which depends on what the question counts.
// What the column says it is counting, and how many of them there were. A question whose figures
// were taken against a base states that instead -- the total would not be what they are shares of.
const buildProgressTitle = ({ answerType, answer, hasBase, hideTotal, rType }) => {
  if ('amount' !== answerType) {
    return hideTotal ? '' : `<small>(N=${formatAmounts(answer, 'total', 0)})</small>`
  }

  if (hasBase) return `<small>(based on ${formatAmounts(answer, ['base', 'total'])} responses)</small>`
  if (hideTotal) return ''

  const total = 'cases' === rType ? 'total' : 'respTotal'
  const label = 'cases' === rType ? 'Respondents' : 'Responses'

  return `${label} <small>(${formatAmounts(answer, total)} total)</small>`
}

// The value cell: what the row plots, and whether it plots a count, a share, or both.
export const calcValueCell = ({ variant, variantAnswer, answerType, preType, rType, isLoading, settled, ignore }) => {
  const cell = { loading: isLoading }

  const count = calcCount({ settled, variantAnswer, answerType })

  if (ignore.percents) {
    cell.amount = variantAnswer[answerType] || 0

    return cell
  }

  if (ignore.amount) {
    cell.percents = variantAnswer.percents || 0

    return cell
  }

  if ('cases' === rType) {
    // a screener variant the pre-question rejected carries no count of its own to show -- and with
    // no count there is no share to show either, or the row prints a percentage beside an empty slot
    const rejected =
      undefined !== preType &&
      undefined !== variant.pre_tag &&
      ((2 === preType && 0 < variant.pre_tag) || (1 === preType && 0 === variant.pre_tag))

    if (rejected && !(0 < variantAnswer[answerType])) return cell

    cell.percents = variantAnswer.percents || 0
    cell.amount = count

    return cell
  }

  if (undefined !== variantAnswer.respPercents) {
    cell.percents = variantAnswer.respPercents || 0
    cell.amount = count

    return cell
  }

  return { ...cell, percents: 0, amount: 0 }
}

// The column the stored sort names, marked with the direction it is sorted in. An order naming a
// column the list does not offer falls to the last one, the way the coffee list resolved it.
const applySortedColumn = (cols, sortOrder) => {
  const parts = String(sortOrder).split('-')

  const found =
    1 < parts.length
      ? cols.findIndex(col => col.param === parts[0])
      : cols.findIndex(col => (('i' === sortOrder[0] && 'title') || 'progress') === col.type)

  const index = 0 > found ? cols.length - 1 : found
  const direction = 2 === parts.length ? ('asc' === parts[1] && 1) || -1 : ('asc' === sortOrder.slice(1) && 1) || -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

export const calcTableProps = ({
  id,
  questionId,
  type,
  preType,
  format,
  components,
  answer,
  answerVariants,
  answerType = 'amount',
  sortOrder = 'iasc',
  clickable = true,
  // the list draws its own magnitudes only where the report draws no chart to read them from
  bars = false,
  handlers = {}
} = {}) => {
  const variants = (answerVariants || []).filter(av => av)
  if (!variants.length) return { cols: [], rows: [] }

  const isRType = calcIsRType({ type })
  const rType = isRType ? getModelValue('rType') : 'cases'

  const traitName = getModelValue('trait')
  const trait = resolveTrait(traitName)
  const traitValues = traitValuesOf(trait)

  const isTraitType = calcIsTraitType({ format, type })
  const isLoading = 'loading' === answer
  const settled = answerSettled(answer)
  const hasBase = answerHasBase(answer)

  const hideTotal = true === format?.hideTotal
  const ignoreRefs = true === format?.ignoreRefs
  const ignoreTraits = true === format?.ignoreTraits
  const ignore = { amount: calcIgnoreAmount(format), percents: calcIgnorePercents(format) }
  const textAmount = true === format?.textAmount

  const percentDecimals = Object.hasOwn(format || {}, 'percentDecimals')
    ? format.percentDecimals
    : getModelValue('percentDecimals')

  const commentsOn =
    (!components || undefined === components.comments || true === components.comments) &&
    'off' !== getFeature('individual_data')?.status?.oe

  const cols = [
    { type: 'title', title: 'Answers', sort: (aRow, bRow, dir) => dir * (aRow.position - bRow.position) },
    {
      type: 'progress',
      title: buildProgressTitle({ answerType, answer, hasBase, hideTotal, rType }),
      percentDecimals,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000)),
      format: value => {
        if (!Number.isFinite(value)) return value

        return textAmount ? value : formatAmounts(value)
      }
    }
  ]

  const rows = variants.map(variant => {
    const variantAnswer = answer?.[`a${variant.position}`] || {}

    const title = {
      title: processText(variant.text),
      colorIndex: trait && isTraitType && !ignoreTraits ? 0 : colorIndexOf(variant.position)
    }

    if (!ignoreRefs) title.label = `A${variant.position}`

    // every answer of a screener says what the screener did with it, including the ones it did
    // nothing to -- an untagged answer is a statement, not an absence
    const screener = screenerState(preType, variant.pre_tag)
    if (screener) title.screener = { state: screener, kind: screenerKind(type) }

    if (Object.hasOwn(variant, 'image')) title.image = variant.image

    if (Object.hasOwn(variant, 'skipsTo')) {
      title.skips_to = variant.skipsTo
      title.onSkipsToClick = handlers.onSkipsToClick
    }

    if (commentsOn && Object.hasOwn(variantAnswer, 'comments')) {
      title.comments = variantAnswer.comments
      title.onCommentsClick = handlers.onCommentsClick
    }

    if (isClickable(format, clickable)) title.onLabelClick = handlers.onLabelClick

    const row = {
      position: variant.position,
      cells: [title, calcValueCell({ variant, variantAnswer, answerType, preType, rType, isLoading, settled, ignore })]
    }

    // a trait breakdown is a table of its own the row expands into
    if (trait && isTraitType && undefined !== variantAnswer[traitName]) {
      row.table = calcTraitTable({
        id,
        trait,
        traitName,
        traitValues,
        traitAnswer: variantAnswer[traitName],
        answerPosition: variant.position
      })
    }

    return row
  })

  // Only the answers set the scale: the largest fills the rail and the rest are read against it.
  if (bars) {
    const measured = rows.filter(row => Number.isFinite(row.cells[1]?.percents))
    const largest = Math.max(...measured.map(row => Math.abs(row.cells[1].percents)), 0)

    if (0 < largest) measured.forEach(row => (row.cells[1].share = Math.abs(row.cells[1].percents) / largest))
  }

  const table = { id, questionId, cols: applySortedColumn(cols, sortOrder), rows, onSort: handlers.onSort }

  if (bars) table.classNames = [...(table.classNames || []), 'bars']

  return table
}

// An answer's comment count opens the comments of the answer it sits on, as the open-ended family's
// does; the table is handed the click, and without it the count is drawn but answers nothing.
export const buildCommentsClick = ({ id, questionId, link, isReportXpert, answerVariants }) => {
  const open = buildOnCommentsClick({ id, questionId, link, isReportXpert })
  return (event, position) => open(event, position, answerVariants || [])
}
